/**
 * Image Upload Utility for imgBB
 * Handles uploading images to imgBB hosting service
 */

const IMGBB_API_KEY = process.env.REACT_APP_IMGBB_API_KEY;
const IMGBB_API_URL = 'https://api.imgbb.com/1/upload';

export interface ImageUploadResult {
  url: string;
  thumbnailUrl: string;
  deleteUrl: string;
  displayUrl: string;
}

/**
 * Upload an image file to imgBB
 * @param file - The image file to upload
 * @returns Promise with image URLs
 */
export const uploadImageToImgBB = async (file: File): Promise<ImageUploadResult> => {
  if (!IMGBB_API_KEY) {
    throw new Error('imgBB API key is not configured. Please set REACT_APP_IMGBB_API_KEY in .env');
  }

  // Validate file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    throw new Error('Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.');
  }

  // Validate file size (max 32MB for imgBB)
  const maxSize = 32 * 1024 * 1024; // 32MB in bytes
  if (file.size > maxSize) {
    throw new Error('File size exceeds 32MB limit.');
  }

  const formData = new FormData();
  formData.append('image', file);
  formData.append('key', IMGBB_API_KEY);

  try {
    const response = await fetch(IMGBB_API_URL, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || 'Failed to upload image');
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error('Image upload failed');
    }

    return {
      url: data.data.url,
      thumbnailUrl: data.data.thumb.url,
      deleteUrl: data.data.delete_url,
      displayUrl: data.data.display_url,
    };
  } catch (error: any) {
    console.error('Image upload error:', error);
    throw new Error(error.message || 'Failed to upload image to imgBB');
  }
};

/**
 * Upload multiple images to imgBB
 * @param files - Array of image files
 * @returns Promise with array of image URLs
 */
export const uploadMultipleImages = async (files: File[]): Promise<ImageUploadResult[]> => {
  const uploadPromises = files.map((file) => uploadImageToImgBB(file));
  return Promise.all(uploadPromises);
};

/**
 * Validate image file before upload
 * @param file - File to validate
 * @returns true if valid, throws error if invalid
 */
export const validateImageFile = (file: File): boolean => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    throw new Error('Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed.');
  }

  const maxSize = 32 * 1024 * 1024; // 32MB
  if (file.size > maxSize) {
    throw new Error('File size exceeds 32MB limit.');
  }

  return true;
};
